// src/utils/tutorUtils.js
import { db, storage } from "../firebaseConfig";
import { collection, addDoc, getDocs, query, where, doc, setDoc, deleteDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

// --- RESOURCES & FOLDERS ---

/**
 * Creates an empty folder (persisted as a metadata document).
 */
export const createNewFolder = async (subject, unit, subcategory, folderName) => {
  await addDoc(collection(db, "tutor_resources"), {
    type: "folder_meta", // Special type just to keep the folder alive
    folder: folderName,
    subject, unit, subcategory,
    createdAt: serverTimestamp()
  });
};

export const uploadResourceFile = async (file, subject, unit, subcategory, folder, parentId = null) => {
  const safeName = file.name;
  const storagePath = `SAT/${subject}/${unit}/${subcategory}/${folder}/${safeName}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  await addDoc(collection(db, "tutor_resources"), {
    name: safeName,
    type: "file",
    url: downloadURL,
    storagePath: storagePath,
    subject, unit, subcategory, folder, parentId,
    createdAt: serverTimestamp()
  });
};

export const addResourceLink = async (name, url, subject, unit, subcategory, folder, parentId = null) => {
  await addDoc(collection(db, "tutor_resources"), {
    name: name,
    type: "link",
    url: url,
    subject, unit, subcategory, folder, parentId,
    createdAt: serverTimestamp()
  });
};

export const deleteResource = async (resourceId, resourceData) => {
  if (resourceData.type === 'file' && resourceData.storagePath) {
    const fileRef = ref(storage, resourceData.storagePath);
    try { await deleteObject(fileRef); } catch (err) { console.warn("File not found", err); }
  }
  await deleteDoc(doc(db, "tutor_resources", resourceId));
};

/**
 * Deletes a custom folder (removes the meta doc). 
 * Note: Does not delete files inside it (tutor must delete those first or we leave them orphaned).
 */
export const deleteFolderMeta = async (subject, unit, subcategory, folderName) => {
  const q = query(
    collection(db, "tutor_resources"),
    where("subject", "==", subject),
    where("unit", "==", unit),
    where("subcategory", "==", subcategory),
    where("folder", "==", folderName),
    where("type", "==", "folder_meta")
  );
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.forEach(d => batch.delete(d.ref));
  await batch.commit();
};

export const fetchResources = async (subject, unit, subcategory) => {
  const q = query(
    collection(db, "tutor_resources"),
    where("subject", "==", subject),
    where("unit", "==", unit),
    where("subcategory", "==", subcategory)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const renameFolder = async (subject, unit, subcategory, oldName, newName) => {
  const q = query(
    collection(db, "tutor_resources"),
    where("subject", "==", subject),
    where("unit", "==", unit),
    where("subcategory", "==", subcategory),
    where("folder", "==", oldName)
  );
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.forEach(docSnap => {
    batch.update(docSnap.ref, { folder: newName });
  });
  await batch.commit();
};

// --- PROGRESS & SCORING ---

export const updateProgress = async (studentId, targetId, data, tutorName) => {
  const docId = `${studentId}_${targetId.replace(/[^a-zA-Z0-9]/g, '_')}`;
  const docRef = doc(db, "student_progress", docId);

  await setDoc(docRef, {
    studentId, targetId, tutorName,
    updatedAt: serverTimestamp(),
    ...data
  }, { merge: true });
};

export const fetchStudentProgress = async (studentId) => {
  const q = query(collection(db, "student_progress"), where("studentId", "==", studentId));
  const snap = await getDocs(q);
  const map = {};
  snap.forEach(d => {
    const data = d.data();
    map[data.targetId] = data;
  });
  return map;
};